import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface User {
    email: string;
    role: string;
    name: string;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private currentUserSubject: BehaviorSubject<User | null>;
    public currentUser$: Observable<User | null>;

    private isAuthenticatedSubject: BehaviorSubject<boolean>;
    public isAuthenticated$: Observable<boolean>;

    // Define valid users with roles
    private validUsers: { [key: string]: { password: string; role: string; name: string } } = {
        'legal@ndaassist.com': {
            password: 'Legal@1',
            role: 'legal',
            name: 'Legal Team'
        }
        // Add more users as needed
        // 'developer@ndaassist.com': {
        //     password: 'Developer@1',
        //     role: 'developer',
        //     name: 'Developer Team'
        // }
    };

    constructor() {
        // Initialize from localStorage
        const storedUser = localStorage.getItem('currentUser');
        this.currentUserSubject = new BehaviorSubject<User | null>(
            storedUser ? JSON.parse(storedUser) : null
        );
        this.currentUser$ = this.currentUserSubject.asObservable();

        this.isAuthenticatedSubject = new BehaviorSubject<boolean>(
            !!storedUser
        );
        this.isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
    }

    public get currentUserValue(): User | null {
        return this.currentUserSubject.value;
    }

    public get isAuthenticated(): boolean {
        return this.isAuthenticatedSubject.value;
    }

    login(email: string, password: string): Observable<{ success: boolean; message: string; user?: User }> {
        return new Observable(observer => {
            // Simulate async operation
            setTimeout(() => {
                const user = this.validUsers[email];

                if (!user) {
                    observer.next({ 
                        success: false, 
                        message: 'Invalid email or password' 
                    });
                    observer.complete();
                    return;
                }

                if (user.password !== password) {
                    observer.next({ 
                        success: false, 
                        message: 'Invalid email or password' 
                    });
                    observer.complete();
                    return;
                }

                const currentUser: User = {
                    email: email,
                    role: user.role,
                    name: user.name
                };

                localStorage.setItem('currentUser', JSON.stringify(currentUser));
                this.currentUserSubject.next(currentUser);
                this.isAuthenticatedSubject.next(true);

                observer.next({ 
                    success: true, 
                    message: 'Login successful',
                    user: currentUser
                });
                observer.complete();
            }, 500);
        });
    }

    logout(): void {
        localStorage.removeItem('currentUser');
        this.currentUserSubject.next(null);
        this.isAuthenticatedSubject.next(false);
    }

    hasRole(role: string): boolean {
        const user = this.currentUserValue;
        return user ? user.role === role : false;
    }

    hasAnyRole(roles: string[]): boolean {
        const user = this.currentUserValue;
        return user ? roles.includes(user.role) : false;
    }
}
